import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useMediaQuery } from 'react-responsive'
import Link from 'next/link'
import { searchVehicles, searchVehiclesKey } from '../../api-client/search'
import useFavorites from '../../hooks/use-favorites'
import { useAuth } from '../../contexts/auth'
import { ROUTES } from '../../constants'
import { AuctionStatusRibbon, GridItem } from '../car-item'
import Loader from '../loader'
import { now } from '../../utils/date-service'
import useOnScreen from '../../hooks/useOnScreen'

// ----------------------------------------------------------------------

const SEARCH_OPTIONS = {
  sortBy: { fieldName: 'auctionTime', sortOrder: 'Ascending' },
  filterBy: {
    multiOptions: [
      {
        fieldName: '',
        values: [],
      },
    ],
    ranges: [
      {
        fieldName: 'year',
        from: '0',
        to: `${now().getFullYear() + 1}`,
      },
      {
        fieldName: 'startingPrice',
        from: '100',
        to: '250000',
      },
      {
        fieldName: 'mileage',
        from: '0',
        to: '200000',
      },
      {
        fieldName: 'mPGCity',
        from: '0',
        to: '150',
      },
    ],
  },
  distance: 250,
  pageIndex: 0,
  excludeCompleted: true,
}

const distinctArrayByProp = (arrayValues, prop) => {
  const result = []
  const map = new Map()
  for (const item of arrayValues) {
    if (!map.has(item[prop])) {
      map.set(item[prop], true) // set any value to Map
      result.push(item)
    }
  }
  return result
}

// ----------------------------------------------------------------------

export default function UpcomingAuctions() {
  const isMobile = useMediaQuery({ query: '(max-width: 1024px)' })
  const pageSize = isMobile ? 4 : 10
  const [pageIndex, setPageIndex] = useState(0)
  const { favorites, toggleFavorite, favoritesProcessing } = useFavorites()
  const [{ isAuthenticated, userDetails }] = useAuth()
  const [searchedVehiclesState, setSearchedVehiclesState] = useState([])
  const [lastPageFetched, setLastPageFetched] = useState(false)

  const auctionSectionRef = useRef()
  const isSectionVisible = useOnScreen({ target: auctionSectionRef })

  const { isLoading, isSuccess } = useQuery({
    queryKey: [searchVehiclesKey, pageIndex],
    queryFn: () =>
      searchVehicles({
        ...SEARCH_OPTIONS,
        pageSize,
        pageIndex,
        location: userDetails.location
          ? { latitude: userDetails.location.lat, longitude: userDetails.location.lng }
          : null,
      }),
    keepPreviousData: true,
    enabled: isSectionVisible,
    onSuccess: (data) => {
      if (!data || !data.vehicles) {
        return
      }
      if (data.isLastPage) {
        setLastPageFetched(true)
      }
      setSearchedVehiclesState((prev) => distinctArrayByProp([...prev, ...data.vehicles], 'id'))
    },
  })

  const lastVehicleRef = useRef()
  const isLastVehicleVisible = useOnScreen({ target: lastVehicleRef, enabled: isSuccess })

  useEffect(() => {
    if (isSuccess && isLastVehicleVisible && !lastPageFetched) {
      setPageIndex((prev) => prev + 1)
    }
  }, [isSuccess, isLastVehicleVisible, lastPageFetched])

  return (
    <div className='container' ref={auctionSectionRef}>
      <div className='headings flex justifyBetween'>
        <h4>Current Listings</h4>
        <Link legacyBehavior href={isAuthenticated ? `${ROUTES.Search}` : ROUTES.Search}>
          <a className='flex alignCenter'>View All Vehicles</a>
        </Link>
      </div>
      <section className='auctionSection'>
        {(isLoading || (isAuthenticated && !userDetails.location)) && (
          <div className='center flex justifyCenter'>
            <Loader />
          </div>
        )}

        {isSuccess && searchedVehiclesState.length === 0 && (
          <div className='center flex justifyCenter'>
            <p className='noAuctionMessage'>No vehicles available near you</p>
          </div>
        )}

        <div className='vehicles'>
          {searchedVehiclesState?.map((vehicle) => {
            const itemProps = {
              key: vehicle.id,
              id: vehicle.id,
              name: vehicle.name,
              trim: vehicle.trim,
              imageFileKey: vehicle.mainPhotoUrl,
              price: vehicle.price,
              mileage: vehicle.mileage,
              isFavorite: !!favorites[vehicle.id]?.isFavorite,
              onFavorite: toggleFavorite,
              isFavoriteProcessing: !!favoritesProcessing[vehicle.id]?.isProcessing,
              leaseAmount:
                vehicle.paymentDetails?.leaseMonthlyPayment !==
                  vehicle.paymentDetails?.loanMonthlyPayment &&
                vehicle.paymentDetails?.leaseMonthlyPayment,
              financeAmount: vehicle.paymentDetails?.loanMonthlyPayment,
              compareIndex: 0,
              href: isAuthenticated ? `/vehicle/${vehicle.id}` : ROUTES.Search,
              ribbon: (
                <AuctionStatusRibbon
                  status={vehicle.status}
                  auctionStartDate={vehicle.auctionStartDate}
                  timeZone={vehicle.timeZone}
                />
              ),
              year: vehicle.year,
            }

            return <GridItem {...itemProps} />
          })}
          {!!searchedVehiclesState && searchedVehiclesState.length > 0 && (
            <div ref={lastVehicleRef} style={{ minWidth: 2 }} />
          )}
        </div>
      </section>

      <style jsx>{`
        .headings {
          padding: 0 5rem 2.6rem 5.5rem;
        }

        h4,
        .noAuctionMessage {
          line-height: 4.3rem;
          font-size: 3.2rem;
        }

        a {
          line-height: 2.9rem;
          font-size: 2.2rem;
        }

        .auctionSection {
          overflow-y: hidden;
          overflow-x: auto;
          white-space: nowrap;
          position: relative;
          height: 24.25rem;
          min-height: 49.8rem;
          padding: 4.8rem 3.3rem;
          background: var(--secondary-input-bg);
        }

        .vehicles {
          display: flex;
          &::before,
          &::after {
            content: '';
            flex: 1;
          }
          grid-gap: 3.1rem 2.1rem;
          grid-template-columns: repeat(auto-fill, 44.4rem);
          width: 100%;
          max-width: 185.5rem;
          margin: 0 auto;

          :global(.carListItem) {
            margin: 0 0 1rem;
          }
        }

        .center {
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
        }

        @media (max-width: 1024px) {
          .container {
            position: relative;
          }

          .headings {
            flex-direction: column;
            align-items: center;
            position: absolute;
            top: 1.7rem;
            z-index: 1;
            width: 100%;
            padding: 0 1rem 2.6rem;
          }

          h4,
          .noAuctionMessage {
            line-height: 2.6rem;
            font-size: 2rem;
          }

          a {
            line-height: 1.6rem;
            font-size: 1.2rem;
          }

          .auctionSection {
            min-height: 45rem;
            padding: 8.1rem 1rem 2.4rem;
          }

          .vehicles {
            display: flex;
            grid-template-columns: auto;
          }
        }
      `}</style>
    </div>
  )
}
